import { __extends } from "tslib";
import { getShadowElement, Platform, removeAllChildren } from '@tyler-components-web/core';
import { BUTTON_CONSTANTS } from '../button';
import { toggleAttribute } from '../core';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { LIST_ITEM_CONSTANTS } from '../list';
import { CalendarView, CALENDAR_CONSTANTS } from './calendar-constants';
import { appendDateContent, appendEvents, setDateAttributes, setDateClasses } from './calendar-utils';
var CalendarAdapter = /** @class */ (function (_super) {
    __extends(CalendarAdapter, _super);
    function CalendarAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._container = getShadowElement(component, CALENDAR_CONSTANTS.selectors.CALENDAR);
        _this._monthButton = getShadowElement(component, CALENDAR_CONSTANTS.selectors.MONTH_BUTTON);
        _this._yearButton = getShadowElement(component, CALENDAR_CONSTANTS.selectors.YEAR_BUTTON);
        _this._previousButton = getShadowElement(component, CALENDAR_CONSTANTS.selectors.PREVIOUS_BUTTON);
        _this._nextButton = getShadowElement(component, CALENDAR_CONSTANTS.selectors.NEXT_BUTTON);
        _this._monthView = getShadowElement(component, CALENDAR_CONSTANTS.selectors.MONTH_VIEW);
        _this._yearView = getShadowElement(component, CALENDAR_CONSTANTS.selectors.YEAR_VIEW);
        _this._viewSwitcher = getShadowElement(component, CALENDAR_CONSTANTS.selectors.VIEW_SWITCHER);
        _this._dateView = getShadowElement(_this._component, CALENDAR_CONSTANTS.selectors.DATE_VIEW);
        _this._header = getShadowElement(_this._component, CALENDAR_CONSTANTS.selectors.HEADER);
        _this._pickerDivider = getShadowElement(_this._component, CALENDAR_CONSTANTS.selectors.PICKER_DIVIDER);
        return _this;
    }
    CalendarAdapter.prototype.registerCalenderHoverListener = function (listener) {
        this._dateView.addEventListener('mouseover', listener);
    };
    CalendarAdapter.prototype.unregisterCalenderHoverListener = function (listener) {
        this._dateView.removeEventListener('mouseover', listener);
    };
    CalendarAdapter.prototype.registerSelectMonthListener = function (listener) {
        this._monthButton.addEventListener('click', listener);
    };
    CalendarAdapter.prototype.registerSelectYearListener = function (listener) {
        this._yearButton.addEventListener('click', listener);
    };
    CalendarAdapter.prototype.registerPreviousMonthListener = function (listener) {
        this._previousButton.addEventListener('click', listener);
    };
    CalendarAdapter.prototype.registerNextMonthListener = function (listener) {
        this._nextButton.addEventListener('click', listener);
    };
    CalendarAdapter.prototype.setPreventFocusListener = function (listener) {
        this._container.addEventListener('mousedown', listener);
    };
    CalendarAdapter.prototype.removePreventFocusListener = function (listener) {
        this._container.removeEventListener('mousedown', listener);
    };
    /**
     * Sets an attribute on the host element.
     * @param name The attribute name.
     * @param value The attribute value.
     */
    CalendarAdapter.prototype.setHostAttribute = function (name, value) {
        if (value === void 0) { value = ''; }
        if (this._component.getAttribute(name) !== value) {
            this._component.setAttribute(name, value);
        }
    };
    /**
     * Sets the displayed month.
     * @param month The month.
     */
    CalendarAdapter.prototype.setMonth = function (month) {
        var locale = Platform.TRIDENT ? 'en-US' : 'default';
        var displayMonth = new Date(2000, month, 2).toLocaleString(locale, { month: 'long' });
        this._container.querySelector(CALENDAR_CONSTANTS.selectors.CURRENT_MONTH).innerHTML = displayMonth;
    };
    /**
     * Sets the displayed year.
     * @param year The year.
     */
    CalendarAdapter.prototype.setYear = function (year) {
        this._container.querySelector(CALENDAR_CONSTANTS.selectors.CURRENT_YEAR).innerHTML = year.toString();
    };
    /**
     * Sets the dates of the displayed month and year.
     * @param dates An array of ICalendarDates.
     */
    CalendarAdapter.prototype.setDates = function (dates, selectedDates, displayOnly, callback) {
        var tableElement = this._container.querySelector(CALENDAR_CONSTANTS.selectors.DATES);
        var hasRange = this._component.hasAttribute(CALENDAR_CONSTANTS.attributes.RANGE);
        removeAllChildren(tableElement);
        // Create a row for each week in the month
        for (var i = 0; i < dates.length / 7; i++) {
            var row = document.createElement('tr');
            row.setAttribute('role', 'row');
            row.setAttribute('id', "date-row-" + i);
            tableElement.appendChild(row);
            for (var d = i * 7; d < (i + 1) * 7; d++) {
                var td = document.createElement('td');
                if (dates[d].isThisMonth) {
                    setDateAttributes(dates[d], td, i, d);
                    setDateClasses(dates[d], td, displayOnly);
                    appendDateContent(dates[d], td);
                    appendEvents(dates[d], td);
                    if (hasRange && selectedDates.length === 2 && selectedDates[1] >= selectedDates[0]) {
                        toggleAttribute(td, dates[d].date > selectedDates[0] && dates[d].date < selectedDates[1], CALENDAR_CONSTANTS.attributes.RANGE_SELECTION);
                        toggleAttribute(td, dates[d].date.toLocaleDateString() === selectedDates[0].toLocaleDateString(), CALENDAR_CONSTANTS.attributes.RANGE_SELECTION_START);
                        toggleAttribute(td, dates[d].date.toLocaleDateString() === selectedDates[1].toLocaleDateString(), CALENDAR_CONSTANTS.attributes.RANGE_SELECTION_END);
                    }
                }
                if (typeof callback === 'function') {
                    callback(dates[d], td);
                }
                row.appendChild(td);
            }
        }
    };
    /**
     * Populates the list of months.
     * @param months An array of months.
     */
    CalendarAdapter.prototype.setMonths = function (months) {
        removeAllChildren(this._monthView);
        var rowNum = 1;
        var _loop_1 = function (month) {
            var tcwButton = document.createElement(BUTTON_CONSTANTS.elementName);
            var button = document.createElement('button');
            var text = new Date(2000, month.month, 1).toLocaleString(undefined, { month: 'short' });
            button.id = "month-" + month.month;
            button.classList.add(CALENDAR_CONSTANTS.classes.MONTH);
            if (month.isActive) {
                button.classList.add(CALENDAR_CONSTANTS.classes.ACTIVE);
            }
            button.textContent = text;
            if (month.disabled) {
                button.disabled = true;
            }
            else {
                button.addEventListener('click', function () { return month.onSelect(month.month); });
            }
            this_1._monthView.appendChild(tcwButton);
            tcwButton.appendChild(button);
            // We need to explicitly set grid and column indexes for IE
            if (Platform.TRIDENT) {
                var index = months.indexOf(month);
                var numColumns = 3;
                var column = (index + 1) % numColumns || numColumns;
                tcwButton.style.setProperty('-ms-grid-row', rowNum.toString());
                tcwButton.style.setProperty('-ms-grid-column', column.toString());
                // IE needs alignment properties...
                tcwButton.style.setProperty('-ms-grid-column-align', 'center');
                tcwButton.style.setProperty('-ms-grid-row-align', 'center');
                if (column % numColumns === 0) {
                    rowNum++;
                }
            }
        };
        var this_1 = this;
        for (var _i = 0, months_1 = months; _i < months_1.length; _i++) {
            var month = months_1[_i];
            _loop_1(month);
        }
        var activeMonthButton = this._monthView.querySelector("button." + CALENDAR_CONSTANTS.classes.ACTIVE);
        if (activeMonthButton) {
            activeMonthButton.classList.add(CALENDAR_CONSTANTS.classes.HIGHLIGHTED);
        }
    };
    /**
     * Populates the list of years.
     * @param years An array of years.
     */
    CalendarAdapter.prototype.setYears = function (years) {
        var list = this._container.querySelector(CALENDAR_CONSTANTS.selectors.YEARS);
        removeAllChildren(list);
        var _loop_2 = function (year) {
            var listItem = document.createElement(LIST_ITEM_CONSTANTS.elementName);
            listItem.id = "year-" + year;
            listItem.value = year.year;
            listItem.classList.add(CALENDAR_CONSTANTS.classes.YEAR);
            if (year.isActive) {
                listItem.classList.add(CALENDAR_CONSTANTS.classes.ACTIVE);
                listItem.setAttribute('id', CALENDAR_CONSTANTS.ids.ACTIVE_YEAR);
            }
            else {
                listItem.setAttribute('id', "y" + year.year.toString());
            }
            listItem.setAttribute(CALENDAR_CONSTANTS.attributes.DATA_YEAR, year.year.toString());
            listItem.textContent = year.year.toString();
            listItem.addEventListener('click', function () { return year.onSelect(year.year); });
            list.appendChild(listItem);
        };
        for (var _i = 0, years_1 = years; _i < years_1.length; _i++) {
            var year = years_1[_i];
            _loop_2(year);
        }
        var activeYearListItem = list.querySelector("[id=" + CALENDAR_CONSTANTS.ids.ACTIVE_YEAR + "]");
        if (activeYearListItem) {
            activeYearListItem.selected = true;
            activeYearListItem.active = true;
        }
    };
    /**
     * Sets the selected year in the list of years
     * @param selectedYear The currently selected year
     */
    CalendarAdapter.prototype.updateYears = function (selectedYear) {
        var oldActiveYear = this._container.querySelector(CALENDAR_CONSTANTS.selectors.ACTIVE_YEAR);
        if (oldActiveYear) {
            var dataYear = oldActiveYear.getAttribute(CALENDAR_CONSTANTS.attributes.DATA_YEAR);
            if (dataYear) {
                if (dataYear === selectedYear.toString()) {
                    this._scrollToActiveYear();
                    return;
                }
                oldActiveYear.setAttribute('id', "y" + dataYear);
            }
            oldActiveYear.classList.remove(CALENDAR_CONSTANTS.classes.ACTIVE);
            var newActiveYear = this._container.querySelector("#y" + selectedYear.toString());
            if (newActiveYear) {
                newActiveYear.setAttribute('id', CALENDAR_CONSTANTS.ids.ACTIVE_YEAR);
                newActiveYear.classList.add(CALENDAR_CONSTANTS.classes.ACTIVE);
            }
        }
        this._scrollToActiveYear();
    };
    /**
     * Sets the active view.
     * @param viewIndex CalendarView of the current view.
     */
    CalendarAdapter.prototype.setActiveView = function (viewIndex) {
        this._viewSwitcher.index = viewIndex;
        if (viewIndex === CalendarView.Year) {
            this.disablePreviousButton();
            this.disableNextButton();
            this._yearButton.style.display = 'none';
            this._monthButton.style.display = 'none';
        }
        else if (viewIndex === CalendarView.Month) {
            this.enablePreviousButton();
            this.enableNextButton();
            this._yearButton.style.display = 'inline-block';
            this._monthButton.style.display = 'none';
        }
        else {
            this.enablePreviousButton();
            this.enableNextButton();
            this._yearButton.style.display = 'inline-block';
            this._monthButton.style.display = 'inline-block';
        }
    };
    /** Disable or enable the previous and next buttons */
    CalendarAdapter.prototype.disablePreviousButton = function () {
        this._previousButton.setAttribute('disabled', '');
    };
    CalendarAdapter.prototype.enablePreviousButton = function () {
        this._previousButton.removeAttribute('disabled');
    };
    CalendarAdapter.prototype.disableNextButton = function () {
        this._nextButton.setAttribute('disabled', '');
    };
    CalendarAdapter.prototype.enableNextButton = function () {
        this._nextButton.removeAttribute('disabled');
    };
    CalendarAdapter.prototype.layout = function () {
        this._previousButton.parentElement.layout();
        this._nextButton.parentElement.layout();
    };
    CalendarAdapter.prototype.setActiveDay = function (_a) {
        var rowIndex = _a[0], columnIndex = _a[1];
        // First we remove the active class from any elements
        var activeElements = Array.from(this._container.querySelectorAll(CALENDAR_CONSTANTS.selectors.ACTIVE_DAY));
        activeElements.forEach(function (element) { return element.classList.remove(CALENDAR_CONSTANTS.classes.DATE_ACTIVE); });
        // Now we set the active class on a specific cell
        var tableBodyElement = this._container.querySelector(CALENDAR_CONSTANTS.selectors.DATES);
        var tr = tableBodyElement.rows[rowIndex];
        if (tr) {
            var td = tr.cells[columnIndex];
            if (td) {
                td.classList.add(CALENDAR_CONSTANTS.classes.DATE_ACTIVE);
            }
        }
    };
    CalendarAdapter.prototype.applyRangePreview = function (event, date, dateRange) {
        if (!date) {
            this._clearRangePreview();
            return;
        }
        var table = this._container.querySelector(CALENDAR_CONSTANTS.selectors.DATES);
        var selected = table.querySelector(CALENDAR_CONSTANTS.selectors.SELECTED_DATE);
        var startDate = selected ? new Date(Date.parse(selected.getAttribute(CALENDAR_CONSTANTS.attributes.DATA_DATE))) : new Date(dateRange.from || 0);
        var endDate = new Date(Date.parse(date.getAttribute(CALENDAR_CONSTANTS.attributes.DATA_DATE)));
        if (endDate < startDate) {
            return;
        }
        var dates = this._getMonthDateElements();
        dates.forEach(function (d) {
            d.removeAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW);
            d.removeAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW_END);
            d.removeAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW_START);
            if (d === selected && selected) {
                if (d === date) {
                    d.setAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW, '');
                }
                selected.setAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW_START, '');
                return;
            }
            else if (d === date && date) {
                date.setAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW_END, '');
            }
            else {
                var dateToCompare = new Date(d.getAttribute(CALENDAR_CONSTANTS.attributes.DATA_DATE));
                if (dateToCompare > startDate && dateToCompare < endDate) {
                    d.setAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW, '');
                }
            }
        });
    };
    /** Scrolls the active year into view, vertically centered */
    CalendarAdapter.prototype._scrollToActiveYear = function () {
        var _this = this;
        var activeYear = this._container.querySelector(CALENDAR_CONSTANTS.selectors.ACTIVE_YEAR);
        if (activeYear) {
            window.requestAnimationFrame(function () {
                _this._yearView.scrollTop = activeYear.offsetTop - _this._yearView.offsetTop - _this._yearView.offsetHeight / 2 + activeYear.offsetHeight / 2;
            });
        }
    };
    CalendarAdapter.prototype._getMonthDateElements = function () {
        var tableElement = this._container.querySelector(CALENDAR_CONSTANTS.selectors.DATES);
        return Array.from(tableElement.querySelectorAll("." + CALENDAR_CONSTANTS.classes.DATE));
    };
    CalendarAdapter.prototype._clearRangePreview = function () {
        var dates = this._getMonthDateElements();
        dates.forEach(function (d) {
            d.removeAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW);
            d.removeAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW_END);
            d.removeAttribute(CALENDAR_CONSTANTS.attributes.RANGE_PREVIEW_START);
        });
    };
    CalendarAdapter.prototype.registerListenerOnDates = function (event, listener) {
        this._dateView.addEventListener(event, listener);
    };
    CalendarAdapter.prototype.unregisterListenerOnDates = function (event, listener) {
        this._dateView.removeEventListener(event, listener);
    };
    CalendarAdapter.prototype.togglePickerDividerVisibility = function (visible) {
        this._pickerDivider.style.display = visible ? 'block' : 'none';
    };
    CalendarAdapter.prototype.toggleHeaderVisibility = function (visible) {
        if (visible) {
            this._header.style.removeProperty('display');
        }
        else {
            this._header.style.display = 'none';
        }
    };
    CalendarAdapter.prototype.getYearCount = function () {
        var list = this._container.querySelector(CALENDAR_CONSTANTS.selectors.YEARS);
        return list ? list.querySelectorAll(LIST_ITEM_CONSTANTS.elementName).length : 0;
    };
    CalendarAdapter.prototype.getActiveYearIndex = function () {
        var list = this._container.querySelector(CALENDAR_CONSTANTS.selectors.YEARS);
        if (!list) {
            return -1;
        }
        var listItems = Array.from(list.querySelectorAll(LIST_ITEM_CONSTANTS.elementName));
        return listItems.findIndex(function (li) { return li.active; });
    };
    CalendarAdapter.prototype.getActiveYear = function () {
        var activeListItem = this._getActiveYearListItem();
        return activeListItem ? activeListItem.value : null;
    };
    CalendarAdapter.prototype.setActiveYearIndex = function (index) {
        var list = this._container.querySelector(CALENDAR_CONSTANTS.selectors.YEARS);
        if (!list) {
            return;
        }
        var listItems = Array.from(list.querySelectorAll(LIST_ITEM_CONSTANTS.elementName));
        listItems.filter(function (li) { return li.active; }).forEach(function (li) { return li.active = false; });
        if (listItems[index]) {
            listItems[index].active = true;
            listItems[index].scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' });
        }
    };
    CalendarAdapter.prototype.getActiveMonth = function () {
        var monthButtons = Array.from(this._container.querySelectorAll("button." + CALENDAR_CONSTANTS.classes.MONTH));
        if (!monthButtons.length) {
            return null;
        }
        return monthButtons.findIndex(function (btn) { return btn.classList.contains(CALENDAR_CONSTANTS.classes.HIGHLIGHTED); });
    };
    CalendarAdapter.prototype.setActiveMonthIndex = function (index) {
        var monthButtons = Array.from(this._container.querySelectorAll("button." + CALENDAR_CONSTANTS.classes.MONTH));
        if (!monthButtons.length) {
            return;
        }
        monthButtons.forEach(function (btn) { return btn.classList.remove(CALENDAR_CONSTANTS.classes.HIGHLIGHTED); });
        if (monthButtons[index]) {
            monthButtons[index].classList.add(CALENDAR_CONSTANTS.classes.HIGHLIGHTED);
        }
    };
    CalendarAdapter.prototype._getActiveYearListItem = function () {
        var list = this._container.querySelector(CALENDAR_CONSTANTS.selectors.YEARS);
        if (!list) {
            return;
        }
        var listItems = Array.from(list.querySelectorAll(LIST_ITEM_CONSTANTS.elementName));
        return listItems.find(function (li) { return li.active; });
    };
    return CalendarAdapter;
}(BaseAdapter));
export { CalendarAdapter };
